import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "hooks/useFirebase";

const STORAGE_KEY = "oko_impersonation";

export const getImpersonatedUserId = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.id || null;
  } catch {
    return null;
  }
};

export const clearImpersonation = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
};

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const Context = createContext(null);

export const ImpersonationProvider = ({ children }) => {
  const [impersonatedUser, setImpersonatedUserState] = useState(readStored);

  const setImpersonatedUser = useCallback((user) => {
    if (!user || !user.id) {
      clearImpersonation();
      setImpersonatedUserState(null);
      return;
    }
    const payload = {
      id: user.id,
      email: user.email || null,
      first_name: user.first_name || null,
      last_name: user.last_name || null,
      display_name: user.display_name || null,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      /* noop */
    }
    setImpersonatedUserState(payload);
  }, []);

  const stopImpersonating = useCallback(() => {
    clearImpersonation();
    setImpersonatedUserState(null);
  }, []);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY) return;
      setImpersonatedUserState(readStored());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        clearImpersonation();
        setImpersonatedUserState(null);
      }
    });
    return () => unsubscribe();
  }, []);

  return (
    <Context.Provider value={{ impersonatedUser, setImpersonatedUser, stopImpersonating }}>
      {children}
    </Context.Provider>
  );
};

export const useImpersonation = () => {
  const ctx = useContext(Context);
  if (!ctx) {
    return { impersonatedUser: null, setImpersonatedUser: () => {}, stopImpersonating: () => {} };
  }
  return ctx;
};
